/**
 * @fileoverview "Not in Grow's list? Describe it", under a plant search that
 * found nothing: a box with what was searched for in it ("bee balm", or "the
 * spiky purple one bees love"), and Ask Grow, which asks what it is (POST
 * /plants/identify). The answer is the "Grow thinks this is …" card
 * (UnknownPlantCard) with one Yes, which files it (useFileSuggestion: a name
 * Grow knows is that plant; under a broad flower parent the flower's own
 * variety; under any other plant the gardener's own plant in its group) and
 * hands the choice back.
 *
 * A name Grow knows is answered free. Anything else is one AI question,
 * counted against the gardener's allowance like a photo guess, which the box
 * says under itself; over the allowance the refusal's own words are shown.
 *
 * Where: PlantIdentityFields (adding a plant, a seed packet's plant) and
 * PileCard (Add photos), under PlantSearchField's "No plant called …".
 * API: POST /plants/identify, POST /plants/own (api/plantNames); filing reads the filed plant's varieties answer for its
 * crop (useFileSuggestion, GET /plants/picker/varieties).
 * Words (en/es): unknown.describe.* (link, label, ask, asking, note, none,
 * failed, other), and UnknownPlantCard's.
 *
 * @module DescribePlant
 */

import React from 'react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { identifyPlant, type PlantSuggestion } from '@/shared/api/plantNames';
import { UnknownPlantCard } from './UnknownPlantCard';
import { useFileSuggestion } from './useFileSuggestion';
import type { CropChoice } from './usePlantPicker';

/** What the box is given. */
interface DescribePlantProps {
  /** What was searched for: the box starts with it. */
  query: string;
  /** The plant was filed: its crop, type and variety, to choose. */
  onChosen: (choice: CropChoice, suggestion: PlantSuggestion) => void;
  /** For the box's id. */
  idPrefix: string;
  /** True while the form is saving. */
  disabled?: boolean;
}

/** The longest description sent; the server cuts at the same (config/plantIdentify TEXT_MAX_CHARS). */
const DESCRIBE_MAX = 120;

/**
 * The link, then the box and Ask Grow, then Grow's answer.
 *
 * @param props - DescribePlantProps.
 */
export const DescribePlant: React.FC<DescribePlantProps> = ({ query, onChosen, idPrefix, disabled = false }) => {
  const { reactTranslate: t } = useTranslation();
  const file = useFileSuggestion();
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(query.trim());
  const [asking, setAsking] = React.useState(false);
  const [filing, setFiling] = React.useState(false);
  const [answer, setAnswer] = React.useState<PlantSuggestion | null>(null);
  /** What to say instead of an answer: Grow could not tell, or the request failed. */
  const [problem, setProblem] = React.useState<string | null>(null);

  /** Asks Grow what it is. */
  const ask = async (): Promise<void> => {
    const typed = text.trim();
    if (!typed) return;
    setAsking(true);
    setProblem(null);
    setAnswer(null);
    try {
      const { suggestion } = await identifyPlant(typed);
      if (suggestion) setAnswer(suggestion);
      else setProblem(t('unknown.describe.none'));
    } catch (error) {
      // Over the allowance, or a failure on our side: the server's words, or the standard sentence (D31).
      setProblem(errorMessage(error, t('unknown.describe.failed')));
    } finally {
      setAsking(false);
    }
  };

  /** Yes: filed, and handed back to choose. */
  const yes = async (): Promise<void> => {
    if (!answer) return;
    setFiling(true);
    setProblem(null);
    try {
      onChosen(await file(answer), answer);
    } catch (error) {
      setProblem(errorMessage(error, t('unknown.describe.failed')));
    } finally {
      setFiling(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setText(query.trim().slice(0, DESCRIBE_MAX)); setOpen(true); }}
        disabled={disabled}
        className="flex min-h-11 items-center text-left text-sm font-medium text-primary underline-offset-4 hover:underline"
      >
        {t('unknown.describe.link')}
      </button>
    );
  }

  return (
    <div className="mt-1 space-y-2">
      <label htmlFor={`${idPrefix}-describe`} className="block text-xs text-muted-foreground">{t('unknown.describe.label')}</label>
      <div className="flex gap-2">
        <Input
          id={`${idPrefix}-describe`}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            // Enter asks rather than sending the whole form.
            if (event.key === 'Enter') { event.preventDefault(); void ask(); }
          }}
          maxLength={DESCRIBE_MAX}
          autoComplete="off"
          disabled={disabled || asking || filing}
        />
        <Button type="button" className="shrink-0" onClick={() => void ask()} disabled={disabled || asking || filing || !text.trim()}>
          {asking ? <Spinner size={16} color="currentColor" /> : t('unknown.describe.ask')}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground" aria-live="polite">{asking ? t('unknown.describe.asking') : t('unknown.describe.note')}</p>
      {answer && (
        <UnknownPlantCard suggestion={answer} onYes={() => void yes()} busy={filing} disabled={disabled} />
      )}
      {problem && <p className="text-sm text-muted-foreground" role="status"><FailureText text={problem} /></p>}
    </div>
  );
};
